/**
 * usePartnerPlaces — the single map <-> partner link.
 *
 * `map_places.advertiser_id` is now the only join between a pin on the Explore
 * map and a partner (advertiser) account. Creating / linking partner records is
 * done through admin-only SECURITY DEFINER functions so the rule lives in one
 * place instead of being duplicated in each admin screen.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type ClaimStatus = "unclaimed" | "pending" | "claimed";

export interface PartnerSummary {
  id: string;
  brandName: string;
  claimStatus: ClaimStatus;
  isVerified: boolean;
}

export interface PartnerPlace {
  id: string;
  name: string;
  emirate: string;
  category: string;
  isActive: boolean;
  photoUrl: string | null;
  offerHeadline: string | null;
}

/** The partner account owned by the signed-in user, if any. */
export function useMyPartner() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["myPartner", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<PartnerSummary | null> => {
      const { data, error } = await (supabase as any)
        .from("advertisers")
        .select("id, brand_name, claim_status, is_verified")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id,
        brandName: data.brand_name,
        claimStatus: (data.claim_status ?? "claimed") as ClaimStatus,
        isVerified: !!data.is_verified,
      };
    },
  });
}

/** The partner record linked to a map place, with its claim state. */
export function usePlacePartner(mapPlaceId?: string) {
  return useQuery({
    queryKey: ["placePartner", mapPlaceId],
    enabled: !!mapPlaceId,
    staleTime: 60_000,
    queryFn: async (): Promise<PartnerSummary | null> => {
      const { data: place, error: placeError } = await (supabase as any)
        .from("map_places")
        .select("advertiser_id")
        .eq("id", mapPlaceId)
        .maybeSingle();
      if (placeError) throw placeError;
      const advertiserId = place?.advertiser_id;
      if (!advertiserId) return null;

      const { data, error } = await (supabase as any)
        .from("advertisers")
        .select("id, brand_name, claim_status, is_verified")
        .eq("id", advertiserId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id,
        brandName: data.brand_name,
        claimStatus: (data.claim_status ?? "claimed") as ClaimStatus,
        isVerified: !!data.is_verified,
      };
    },
  });
}

/** Places linked to a partner account (public read for active pins). */
export function usePartnerPlaces(advertiserId?: string) {
  return useQuery({
    queryKey: ["partnerPlaces", advertiserId],
    enabled: !!advertiserId,
    staleTime: 60_000,
    queryFn: async (): Promise<PartnerPlace[]> => {
      const { data, error } = await (supabase as any)
        .from("map_places")
        .select("id, name, emirate, category, is_active, photo_url, offer_headline")
        .eq("advertiser_id", advertiserId)
        .order("name", { ascending: true })
        .limit(200);
      if (error) throw error;
      return (data ?? []).map((r: any) => ({
        id: r.id,
        name: r.name,
        emirate: r.emirate,
        category: r.category,
        isActive: r.is_active !== false,
        photoUrl: r.photo_url ?? null,
        offerHeadline: r.offer_headline ?? null,
      }));
    },
  });
}

function useInvalidatePartnerLinks() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["placePartner"] });
    qc.invalidateQueries({ queryKey: ["partnerPlaces"] });
    qc.invalidateQueries({ queryKey: ["placeAdvertiserId"] });
    qc.invalidateQueries({ queryKey: ["mapPlaces"] });
    qc.invalidateQueries({ queryKey: ["admin-advertisers"] });
  };
}

/** Admin: find-or-create the partner record for a place and link it. */
export function useEnsurePartnerForPlace() {
  const invalidate = useInvalidatePartnerLinks();
  return useMutation({
    mutationFn: async (args: {
      placeId: string;
      ownerUserId?: string | null;
      contactEmail?: string | null;
      contactPhone?: string | null;
    }): Promise<string> => {
      const { data, error } = await (supabase as any).rpc("ensure_partner_for_place", {
        p_place_id: args.placeId,
        p_owner_user_id: args.ownerUserId ?? null,
        p_contact_email: args.contactEmail ?? null,
        p_contact_phone: args.contactPhone ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });
}

/** Admin: link a place to an existing partner record, or unlink with null. */
export function useLinkPlaceToPartner() {
  const invalidate = useInvalidatePartnerLinks();
  return useMutation({
    mutationFn: async (args: { placeId: string; advertiserId: string | null }) => {
      const { error } = await (supabase as any).rpc("link_place_to_partner", {
        p_place_id: args.placeId,
        p_advertiser_id: args.advertiserId,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export interface PartnerLinkSummary {
  claimStatus: ClaimStatus;
  placeCount: number;
}

/**
 * Admin overview: claim state + linked place count for every partner,
 * keyed by advertiser id. One pair of queries instead of per-row lookups.
 */
export function usePartnerLinkSummaries() {
  return useQuery({
    queryKey: ["partnerLinkSummaries"],
    staleTime: 60_000,
    queryFn: async (): Promise<Record<string, PartnerLinkSummary>> => {
      const [{ data: advertisers, error: aErr }, { data: places, error: pErr }] = await Promise.all([
        (supabase as any).from("advertisers").select("id, claim_status").limit(1000),
        (supabase as any).from("map_places").select("advertiser_id").not("advertiser_id", "is", null).limit(1000),
      ]);
      if (aErr) throw aErr;
      if (pErr) throw pErr;

      const counts = new Map<string, number>();
      for (const row of places ?? []) {
        counts.set(row.advertiser_id, (counts.get(row.advertiser_id) ?? 0) + 1);
      }
      const out: Record<string, PartnerLinkSummary> = {};
      for (const a of advertisers ?? []) {
        out[a.id] = {
          claimStatus: (a.claim_status ?? "claimed") as ClaimStatus,
          placeCount: counts.get(a.id) ?? 0,
        };
      }
      return out;
    },
  });
}
